import { useMemo } from 'react';
import { TODO_VIEW_MODES } from '../constants';
import {
  buildNestedTree,
  buildSimpleList,
  buildSwimlaneGroups,
  sortItems,
  sortTree,
} from '../utils/todoHelpers';
import { calculateLockStatesWithParentContext } from '../../FlowchartView/utils/dependencyUtils';
import { useCampaignProgressStats } from '../../shared/CampaignHeader/hooks/useCampaignProgressStats';
import { filterActionableTasks } from '../../shared/utils/taskAvailabilityUtils';
import { applyFilters } from '../../shared/filterUtils';
import { groupTasksIntoSwimlanes } from '../../config/swimlaneConfig';

/**
 * Hook for computing all derived data in TodoListView
 * Handles: filtered tasks, nested task flattening, lock states, view data, and stats
 * 
 * View mode is now derived from swimlaneSetting and showNestedItems:
 * - swimlaneSetting === 'none' && showNestedItems === true → MASTER (nested tree)
 * - swimlaneSetting === 'none' && showNestedItems === false → SIMPLE (flat list)
 * - swimlaneSetting !== 'none' → SWIMLANE (grouped by swimlane setting)
 */
export function useTodoListData({
  tasks,
  statuses,
  allTags,
  nestedSprints,
  allSprints,
  showArchived,
  showActionableOnly = false,
  viewMode, // Legacy - still supported for backwards compatibility
  filters = {},  // Shared filter object from KanbanFilterDropdown
  sortBy,
  swimlaneGroupBy, // Legacy - for old swimlane mode
  // New props for unified swimlane approach
  swimlaneSetting = 'none', // Same as Kanban: 'none', 'tag', 'priority', 'difficulty', etc.
  showNestedItems = true, // Toggle for showing nested items (replaces Master mode)
  // Custom fields for filtering
  customFields = [],
  localSprint = null,
}) {
  // Calculate lock states early for filtering (needed for mission type filters)
  const hasMissionTypeFilter = filters?.missionType?.length > 0;
  const earlyLockStates = useMemo(() => {
    if (!showActionableOnly && !hasMissionTypeFilter) return {};
    return calculateLockStatesWithParentContext(tasks, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
    });
  }, [tasks, nestedSprints, statuses, showActionableOnly, hasMissionTypeFilter, localSprint, allSprints]);

  // Apply shared filters (same as Kanban/Table/Gantt/Flow, including custom fields)
  const baseFilteredTasks = useMemo(() => {
    return applyFilters(tasks, filters, showArchived, earlyLockStates, nestedSprints, statuses, false, customFields);
  }, [tasks, filters, showArchived, earlyLockStates, nestedSprints, statuses, customFields]);

  // Apply actionable filter if enabled
  const filteredTasks = useMemo(() => {
    if (!showActionableOnly) return baseFilteredTasks;
    return filterActionableTasks(baseFilteredTasks, earlyLockStates, nestedSprints, statuses);
  }, [baseFilteredTasks, showActionableOnly, earlyLockStates, nestedSprints, statuses]);

  // Build a flat list of ALL tasks including from nested sprints (for lock state calculation)
  const allTasksFlat = useMemo(() => {
    const allTasks = [...tasks];
    const visited = new Set();
    
    const collectNestedTasks = (sprintTasks) => {
      for (const task of sprintTasks) {
        if (task.nestedSprintId && !visited.has(task.nestedSprintId)) {
          visited.add(task.nestedSprintId);
          const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
          if (nestedSprint?.tasks) {
            allTasks.push(...nestedSprint.tasks);
            collectNestedTasks(nestedSprint.tasks);
          }
        }
      }
    };
    
    collectNestedTasks(tasks);
    return allTasks;
  }, [tasks, allSprints]);

  // Calculate lock states for dependencies
  const lockStates = useMemo(() => {
    const states = calculateLockStatesWithParentContext(allTasksFlat, nestedSprints, statuses, {
      currentSprint: localSprint,
      allSprints,
    });
    
    if (process.env.NODE_ENV === 'development') {
      const lockedTasks = Object.entries(states).filter(([_, s]) => s.isLocked);
      if (lockedTasks.length > 0) {
        console.log('[TodoListView] Found', lockedTasks.length, 'tasks with blocking dependencies');
      }
    }
    
    return states;
  }, [allTasksFlat, nestedSprints, statuses, localSprint, allSprints]);

  // Derive effective view mode from swimlaneSetting and showNestedItems
  // This provides backwards compatibility while supporting the new unified approach
  const effectiveViewMode = useMemo(() => {
    // If using new swimlaneSetting prop
    if (swimlaneSetting && swimlaneSetting !== 'none') {
      return TODO_VIEW_MODES.SWIMLANE;
    }
    // Use showNestedItems to determine between MASTER and SIMPLE
    return showNestedItems ? TODO_VIEW_MODES.MASTER : TODO_VIEW_MODES.SIMPLE;
  }, [swimlaneSetting, showNestedItems]);

  // Swimlane context for groupTasksIntoSwimlanes (same as Kanban)
  const swimlaneContext = useMemo(() => ({
    allTags,
    nestedSprints,
    hasPriorityField: false,
    hasAssigneeField: false,
    hasDueDateField: false,
  }), [allTags, nestedSprints]);

  // Build data structures based on effective view mode
  // Note: Filtering is now done at the task level via applyFilters above,
  // so we no longer need filterItems here
  const viewData = useMemo(() => {
    // Use legacy viewMode if provided and swimlaneSetting is not set
    const mode = viewMode && !swimlaneSetting ? viewMode : effectiveViewMode;
    
    switch (mode) {
      case TODO_VIEW_MODES.MASTER: {
        let tree = buildNestedTree(filteredTasks, allSprints, 0, new Set(), null, null);
        tree = sortTree(tree, sortBy, statuses, customFields);
        return { type: 'tree', data: tree };
      }
      case TODO_VIEW_MODES.SIMPLE: {
        let items = buildSimpleList(filteredTasks, allSprints);
        items = sortItems(items, sortBy, statuses, customFields);
        return { type: 'list', data: items };
      }
      case TODO_VIEW_MODES.SWIMLANE: {
        // Use new swimlaneSetting if available, otherwise fall back to legacy swimlaneGroupBy
        const effectiveSwimlaneBy = swimlaneSetting && swimlaneSetting !== 'none' 
          ? swimlaneSetting 
          : swimlaneGroupBy;
        
        // Helper to build nested children for a task (when showNestedItems is true)
        const buildNestedChildren = (task, depth, visitedSprintIds) => {
          if (!showNestedItems || !task.nestedSprintId) return [];
          
          const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
          if (!nestedSprint?.tasks || visitedSprintIds.has(nestedSprint.id)) return [];
          
          const newVisited = new Set(visitedSprintIds);
          newVisited.add(nestedSprint.id);
          
          const children = [];
          nestedSprint.tasks.forEach(nestedTask => {
            const nestedNestedSprint = allSprints.find(s => s.id === nestedTask.nestedSprintId);
            const hasNestedContent = nestedNestedSprint?.tasks?.length > 0;
            
            // Add the nested task
            children.push({
              task: nestedTask,
              depth: depth + 1,
              isSubtask: false,
              nestedSprint: nestedNestedSprint,
              isParent: hasNestedContent && !newVisited.has(nestedTask.nestedSprintId),
              hasNestedCampaign: !!nestedTask.nestedSprintId,
              sprintTasks: nestedSprint.tasks,
              sprintId: nestedSprint.id,
            });
            
            // Recursively add its children
            if (hasNestedContent) {
              children.push(...buildNestedChildren(nestedTask, depth + 1, newVisited));
            }
          });
          
          return children;
        };
        
        // Use the shared groupTasksIntoSwimlanes for consistency with Kanban
        if (swimlaneSetting && swimlaneSetting !== 'none') {
          const swimlanes = groupTasksIntoSwimlanes(filteredTasks, swimlaneSetting, swimlaneContext);
          if (swimlanes) {
            // Convert to the format expected by TodoListContent
            const groups = swimlanes.map(swimlane => {
              const items = [];
              
              swimlane.tasks.forEach(task => {
                const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
                const hasNestedContent = nestedSprint?.tasks?.length > 0;
                
                // Add the parent task
                items.push({
                  task,
                  depth: 0,
                  isSubtask: false,
                  nestedSprint,
                  isParent: showNestedItems && hasNestedContent,
                  hasNestedCampaign: !!task.nestedSprintId,
                  sprintTasks: filteredTasks,
                });
                
                // If showNestedItems is true, add nested children (they follow the parent, not swimlane logic)
                if (showNestedItems && hasNestedContent) {
                  items.push(...buildNestedChildren(task, 0, new Set()));
                }
              });
              
              return {
                id: swimlane.id,
                label: swimlane.label,
                color: swimlane.color,
                order: swimlane.order,
                items: sortItems(items, sortBy, statuses, customFields),
              };
            });
            
            return { type: 'swimlane', data: groups, showNestedItems };
          }
        }
        
        // Fall back to legacy buildSwimlaneGroups
        let items = buildSimpleList(filteredTasks, allSprints);
        items = items.filter(item => !item.isSubtask);
        const groups = buildSwimlaneGroups(
          items.map(i => i.task),
          allSprints,
          statuses,
          allTags,
          effectiveSwimlaneBy
        );
        groups.forEach(g => {
          g.items = sortItems(g.items, sortBy, statuses, customFields);
        });
        return { type: 'swimlane', data: groups, showNestedItems };
      }
      default:
        return { type: 'list', data: [] };
    }
  }, [viewMode, effectiveViewMode, filteredTasks, allSprints, statuses, allTags, sortBy, swimlaneGroupBy, swimlaneSetting, swimlaneContext, showNestedItems, customFields]);

  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(filteredTasks, statuses, nestedSprints, allSprints);

  return {
    filteredTasks,
    allTasksFlat,
    lockStates,
    viewData,
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  };
}
